/**
 * @file SocketContext.tsx
 * @description
 * React Context for managing the Socket.io connection.
 * Provides the socket instance and connection status to components.
 * Automatically connects to the Socket.io server when the user is authenticated.
 * Manages the socket instance using a ref to prevent infinite render/effect loops
 * caused by state updates within effect cleanups.
 *
 * @dependencies
 * - react: Standard React library for Context and hooks (including useRef).
 * - socket.io-client: For establishing the WebSocket connection.
 * - ../contexts/AuthContext: For accessing the authentication status.
 */

import React, { createContext, useContext, useEffect, useState, ReactNode, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';

// Define the type for the Socket context value
interface SocketContextType {
  socket: Socket | null; // The Socket.io client instance, or null if not connected
  isConnected: boolean;  // Connection status
  error: Error | null; // Any connection error
}

// Create the context with a default undefined value
export const SocketContext = createContext<SocketContextType | undefined>(undefined);

/**
 * Custom hook to easily access the SocketContext.
 * Throws an error if used outside of a SocketProvider.
 */
export const useSocket = () => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

/**
 * SocketProvider component that wraps parts of the application needing socket communication.
 * Establishes and manages the Socket.io connection based on authentication status.
 * Uses a ref to hold the mutable socket instance and state variables for consumers.
 */
export const SocketProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth(); // Only need isAuthenticated for deciding when to connect/disconnect

  // Use a ref to hold the mutable socket instance.
  // Changes to a ref do not trigger component re-renders or effect re-runs.
  const socketRef = useRef<Socket | null>(null);

  // State variables for context consumers.
  // These are updated by event listeners attached to the socket instance held in the ref.
  const [socketState, setSocketState] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  console.log('SocketProvider: Rendering.', { isAuthenticated, socketRefExists: !!socketRef.current, socketStateExists: !!socketState, isConnectedState: isConnected });

  // This effect manages the lifecycle of the socket instance.
  // It runs primarily when `isAuthenticated` changes.
  useEffect(() => {
    console.log('SocketProvider Effect: Running.', { isAuthenticated, currentSocketRef: !!socketRef.current });

    // --- Cleanup function defined inside the effect ---
    // This cleanup runs when dependencies (`isAuthenticated`) change or component unmounts.
    // It disconnects the socket instance currently held in the ref and clears the ref.
    const cleanupSocket = () => {
      console.log('SocketProvider Effect Cleanup: Running.');
      const currentSocket = socketRef.current;
      if (currentSocket) {
        console.log('SocketProvider Effect Cleanup: Removing listeners and disconnecting socket.');
        // Remove all listeners to prevent state updates from a disconnected socket
        currentSocket.offAny();
        currentSocket.removeAllListeners();

        // Disconnect the socket (handles connecting or connected states)
        if (currentSocket.connected) {
          console.log('SocketProvider Effect Cleanup: Explicitly disconnecting.');
          currentSocket.disconnect();
        } else {
          console.log('SocketProvider Effect Cleanup: Socket not connected/connecting. Calling disconnect (safe).');
           currentSocket.disconnect(); // Call disconnect even if not connected to stop attempts
        }

        // Clear the ref and reset state variables
        socketRef.current = null;
        setSocketState(null); // Clear the state reference visible to consumers
        setIsConnected(false);
        setError(null);
        console.log('SocketProvider Effect Cleanup: State and ref cleared.');
      } else {
        console.log('SocketProvider Effect Cleanup: No socket instance in ref to clean up.');
      }
    };


    // Condition 1: Authenticated and no socket instance exists in the ref. Create and connect.
    // We check `socketRef.current === null` to ensure we only create a new instance once
    // per authentication session unless the existing one is explicitly cleaned up.
    if (isAuthenticated === true && socketRef.current === null) {
      console.log(`SocketProvider Effect: Authenticated and no socket exists in ref. Creating new socket...`);

      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      if (!backendUrl) {
           console.error('SocketProvider Effect: REACT_APP_BACKEND_URL is not set.');
           setError(new Error('Backend URL is not configured. Cannot connect to server.'));
           setIsConnected(false);
           // Return cleanup (which will be a no-op in this case)
           return cleanupSocket;
      }

      console.log(`SocketProvider Effect: Attempting socket connection to URL: "${backendUrl}"`);

      try {
        const newSocket = io(backendUrl, {
          withCredentials: true,
          transports: ['websocket', 'polling'],
          autoConnect: false, // We will manage connect/disconnect
        });

        // Store the new socket instance in the ref immediately.
        // This does NOT trigger an effect re-run based on dependency changes.
        socketRef.current = newSocket;
        // Update the state variable so consumers get the instance.
        setSocketState(newSocket);

         // --- Set up event listeners ---
         // These listeners update state variables visible to context consumers.
         // They should NOT update the `socketRef.current` or call `setSocketState(null)`.
         const handleConnect = () => {
           console.log('SocketContext (Listener): Socket connected:', newSocket.id);
           setIsConnected(true);
           setError(null); // Clear any previous errors on successful connect
         };

         const handleDisconnect = (reason: string) => {
           console.log('SocketContext (Listener): Socket disconnected:', reason);
           setIsConnected(false);
           setError(new Error(`Socket disconnected: ${reason}`)); // Set a disconnection error message
         };

         const handleConnectError = (err: Error) => {
           console.error('SocketContext (Listener): Socket connection error!', err);
           setIsConnected(false);
           setError(err); // Set the connection error
         };

         // Attach listeners to the newly created socket instance
         console.log('SocketProvider Effect: Attaching listeners.');
         newSocket.on('connect', handleConnect);
         newSocket.on('disconnect', handleDisconnect);
         newSocket.on('connect_error', handleConnectError);


        // Explicitly connect the socket
        console.log('SocketProvider Effect: Calling newSocket.connect()');
        newSocket.connect();

         // Set initial state to connecting while waiting for 'connect' or 'connect_error'
         setIsConnected(false); // It's not yet connected
         setError(null); // Clear previous errors when starting a new attempt

        // Return the cleanup function. This cleanup is associated with THIS effect run
        // and will clean up the `newSocket` instance created here.
        return cleanupSocket;

      } catch (err) {
         console.error('SocketProvider Effect: Error creating or connecting socket:', err);
         setError(err as Error);
         setIsConnected(false);
          // Clear the ref and state if creation failed
          socketRef.current = null;
          setSocketState(null);
         return cleanupSocket; // Return cleanup (which will be a no-op if ref is null)
      }

    }
    // Condition 2: Not authenticated AND a socket instance exists in the ref. Disconnect and clean up.
    // This happens when isAuthenticated changes from true to false.
    else if (isAuthenticated === false && socketRef.current !== null) {
         console.log(`SocketProvider Effect: User logged out or auth failed (${isAuthenticated}). Socket exists in ref. Running cleanup.`);
         // The cleanup function associated with the *previous* effect run (the one that created the socket)
         // will be automatically called by React because 'isAuthenticated' (dependency) changed.
         // That cleanup function handles the disconnection and clearing the ref/state.
         // So, no explicit action needed here in the main effect body, the cleanup handles it.
         // We still need to return a cleanup function for *this* effect run (which will be a no-op).
         return cleanupSocket; // Return cleanup (it will find socketRef.current is null after the previous cleanup runs)
    }
     // Condition 3: isAuthenticated is null (checking) or isAuthenticated is true but socket *already* exists in ref.
     // Do nothing in the main effect body regarding creation/destruction.
     // This ensures we don't create duplicate sockets or constantly disconnect/reconnect.
     // The effect just needs to return the cleanup function associated with the *current* socket instance in the ref.
     else {
          console.log(`SocketProvider Effect: Conditions not met for create/destroy logic in main body. isAuthenticated=${isAuthenticated}, currentSocketRef=${!!socketRef.current}`);
           // Return the cleanup function. If socketRef.current is null, cleanup is no-op.
           // If socketRef.current exists, this cleanup will clean up that instance
           // if isAuthenticated changes or component unmounts.
          return cleanupSocket;
     }

     // Effect dependencies: Only isAuthenticated.
     // `socketRef.current` is NOT a dependency because changes to refs should not trigger effects.
     // `socketState` is managed by the effect, NOT a trigger for it.
  }, [isAuthenticated]); // Dependency is now only isAuthenticated


  // Provide the state variables via the context
  const contextValue: SocketContextType = {
    socket: socketState, // Expose state variable (set from ref updates)
    isConnected,
    error, // Expose connection error
  };

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};